"use client";

import * as React from "react";
import { Check, Hash, Share2 } from "lucide-react";

import { Button } from "./button";

export interface BookingShareProps {
  bookingRef: string;
  reservationUrl: string;
  shareMessage?: string;
  shareTitle?: string;
  refLabel: string;
  shareLabel: string;
  copiedLabel: string;
  onCopied?: () => void;
  onCopyError?: () => void;
}

export function BookingShare({
  bookingRef,
  reservationUrl,
  shareMessage,
  shareTitle,
  refLabel,
  shareLabel,
  copiedLabel,
  onCopied,
  onCopyError,
}: BookingShareProps) {
  const [copied, setCopied] = React.useState(false);

  const fallbackCopy = async () => {
    try {
      await navigator.clipboard.writeText(reservationUrl);
      setCopied(true);
      onCopied?.();
      window.setTimeout(() => setCopied(false), 2000);
      return;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = reservationUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        onCopied?.();
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        onCopyError?.();
      }
      document.body.removeChild(ta);
    }
  };

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareMessage,
          url: reservationUrl,
        });
        return;
      } catch (err) {
        // User dismissed the share sheet — don't surface a copy fallback.
        if ((err as DOMException)?.name === "AbortError") return;
        // Any other failure: fall through to clipboard copy.
      }
    }
    await fallbackCopy();
  };

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <Hash className="w-5 h-5 text-primary-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {refLabel}
          </p>
          <p className="font-mono font-semibold text-neutral-800 dark:text-neutral-100 break-all">
            {bookingRef}
          </p>
        </div>
      </div>
      <Button
        type="button"
        onClick={handleShare}
        variant="outline"
        size="sm"
        className="rounded-full cursor-pointer h-8 px-3 text-xs shrink-0"
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5 mr-1.5" />
            {copiedLabel}
          </>
        ) : (
          <>
            <Share2 className="w-3.5 h-3.5 mr-1.5" />
            {shareLabel}
          </>
        )}
      </Button>
    </div>
  );
}
